import { AppContext } from 'provider/AppProvider/AppProvider';
import { useDefaultColorValue } from 'services/queries/app';
import { FC, useContext, useMemo } from 'react';
import VideoPlayer from 'components/modules/VideoPlayer';
import { nanoid } from 'nanoid';
import { darken } from 'polished';
import { generateUrl } from 'utils/url';
import { HeroProps } from './Hero.interface';
import FeaturedContent from './FeaturedContent';
import { StyledHeader } from './Hero.styles';

const Hero: FC<HeroProps> = ({
  imgSrc,
  headline,
  useFullscreen,
  videoSrc,
  featuredButtons,
}) => {
  const {
    setup,
    baseURL,
    config: { defaultLabels },
    about: { platform_version },
  } = useContext(AppContext);

  const colors = useDefaultColorValue();

  // internal buttons with override labels and internal urls including enabled status
  const internalButtons = [
    {
      target: 'Directories',
      target_label: String(
        setup.header.menu?.label_overrides.directories === ''
          ? defaultLabels.directories
          : setup.header.menu?.label_overrides.directories,
      ),
      target_url: `${String(baseURL)}/directories`,
      enabled: setup.header.menu?.enabled_modules.includes('directories'),
    },
    {
      target: 'Track Now',
      target_label: String(
        setup.header.menu?.label_overrides.track_now === '' ||
          !setup.header.menu?.label_overrides.track_now
          ? defaultLabels.track_now
          : setup.header.menu?.label_overrides.track_now,
      ),
      target_url: `${String(baseURL)}/unlock-code`,
      enabled: true,
    },
    {
      target: 'Tips & Posts',
      target_label: String(
        setup.header.menu?.label_overrides.tips === ''
          ? defaultLabels.tips
          : setup.header.menu?.label_overrides.tips,
      ),
      target_url: `${String(baseURL)}/posts`,
      enabled: setup.header.menu?.enabled_modules.includes('tips'),
    },
    {
      target: 'Topics',
      target_label: String(
        setup.header.menu?.label_overrides.topics === ''
          ? defaultLabels.topics
          : setup.header.menu?.label_overrides.topics,
      ),
      target_url: `${String(baseURL)}/topics`,
      enabled: setup.header.menu?.enabled_modules.includes('topics'),
    },
    {
      target: 'Courses',
      target_label: String(
        setup.header.menu?.label_overrides.courses === ''
          ? defaultLabels.courses
          : setup.header.menu?.label_overrides.courses,
      ),
      target_url: `${String(baseURL)}/courses`,
      enabled: setup.header.menu?.enabled_modules.includes('courses'),
    },
    {
      target: 'Action Lists',
      target_label: String(
        setup?.header?.menu?.label_overrides?.action_lists === ''
          ? defaultLabels.action_lists
          : setup?.header?.menu?.label_overrides?.action_lists,
      ),
      target_url: `${String(baseURL)}/action-lists`,
      enabled: setup.header.menu?.enabled_modules.includes('action_lists'),
    },
  ];

  // filter for disabled menu buttons
  const enabledButtons = internalButtons.filter((button) => button.enabled);

  // generate final button data
  const getButton = (buttonTarget: {
    target: string;
    target_label: string;
    target_url: string;
  }) => {
    // if Custom URL return original data
    if (buttonTarget.target === 'Custom URL') {
      return buttonTarget;
    }

    return (
      // only return active buttons
      enabledButtons.find((button) => button.target === buttonTarget.target) ??
      undefined
    );
  };
  // button will be undefined if it is inactive so it won't be shown
  const leftButton = useMemo(() => {
    if (parseFloat(platform_version) === 8)
      return {
        target: 'Unlock Code',
        target_label: 'Unlock Code',
        target_url: `${String(baseURL)}/unlock-code`,
        enabled: true,
      };
    return featuredButtons?.left !== undefined
      ? getButton({
          target: featuredButtons?.left.target,
          target_label: featuredButtons?.left.target_label,
          target_url: featuredButtons?.left.target_url,
        })
      : undefined;
  }, []);
  const rightButton = useMemo(() => {
    if (parseFloat(platform_version) === 8)
      return internalButtons.find((a) => a.target === 'Topics');

    return featuredButtons?.right !== undefined
      ? getButton({
          target: featuredButtons?.right.target,
          target_label: featuredButtons?.right.target_label,
          target_url: featuredButtons?.right.target_url,
        })
      : undefined;
  }, []);

  let imgFullScreenBgSrc = '';
  let showImage = false;
  let showVideo = false;

  if (videoSrc !== '' && videoSrc != null) {
    showVideo = true;
  }

  if (useFullscreen === true) {
    if (imgSrc !== '' && imgSrc != null) {
      imgFullScreenBgSrc = imgSrc;
    }
  }

  if (useFullscreen !== true && Boolean(imgSrc)) {
    showImage = true;
  }

  if (showImage && imgFullScreenBgSrc === '' && Boolean(videoSrc)) {
    showImage = false;
  }

  return (
    <section
      key={imgFullScreenBgSrc}
      style={{
        background:
          imgFullScreenBgSrc === ''
            ? `radial-gradient(at center center, ${colors.dark}, ${darken(
                0.1,
                colors.dark,
              )})`
            : `url(${imgFullScreenBgSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      }}
      className="flex flex-col justify-center py-16"
    >
      <div className="container mx-auto px-0 xl:px-16 min-h-[70vh] xl:min-h-[60vh] flex flex-col">
        <div className="my-auto">
          <StyledHeader
            font={setup?.fonts?.header}
            className="text-5xl text-center mx-auto mb-6 text-white"
          >
            {headline}
          </StyledHeader>
          {showVideo && (
            <div className="flex flex-col px-4 py-8 lg:p-0 items-center justify-center xl:w-[1056px] xl:h-[575px] mx-auto">
              <VideoPlayer url={videoSrc!} />
            </div>
          )}
          {showImage && imgSrc && (
            <div className="flex flex-col px-4 py-8 lg:p-0 items-center justify-center xl:w-[1056px] mx-auto">
              <img
                className="max-w-full object-scale-down rounded-2xl"
                src={generateUrl(imgSrc)}
                alt="headline-img"
              />
            </div>
          )}
          <div className="flex flex-row justify-center gap-1 md:gap-4 mt-16 lg:mt-12 mx-2.5 md:mx-0">
            {(leftButton?.target_label ?? leftButton?.target) && (
              <FeaturedContent
                key={`btn-${nanoid()}`}
                target={leftButton?.target}
                target_url={leftButton?.target_url}
                target_label={leftButton?.target_label}
              />
            )}

            {(rightButton?.target_label ?? rightButton?.target) && (
              <FeaturedContent
                key={`btn-${nanoid()}`}
                target={rightButton?.target}
                target_url={rightButton?.target_url}
                target_label={rightButton?.target_label}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
